import React, {useEffect, useState} from 'react';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {FixedSizeList} from 'react-window';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@material-ui/icons/KeyboardArrowRightRounded';
import {fade, makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import InputBase from '@material-ui/core/InputBase';
import JString from '@easytech-international-sdn-bhd/jstring';
import Checkbox from '@material-ui/core/Checkbox';

const useStyles = makeStyles(theme => ({
	paper: {
		borderRadius: 12,
		overflow: 'hidden',
	},
	dialogTitle: {
		padding: theme.spacing(3, 3, 2, 3),
	},
	titleRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: theme.spacing(2),
	},
	titlePrimary: {
		fontSize: '1.25rem',
		fontWeight: 600,
		lineHeight: 1.3,
		color: theme.palette.text.primary,
	},
	titleLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		flexWrap: 'wrap',
	},
	selectionBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: theme.spacing(0.25, 1),
		borderRadius: 999,
		backgroundColor: fade(theme.palette.primary.main, 0.16),
		color: theme.palette.type === 'light' ? '#7a6f00' : theme.palette.primary.main,
		fontSize: '0.6875rem',
		fontWeight: 700,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
	},
	searchRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		marginTop: theme.spacing(2),
	},
	search: {
		position: 'relative',
		flex: 1,
		borderRadius: 12,
		backgroundColor: fade(theme.palette.text.primary, 0.04),
		border: `1px solid ${fade(theme.palette.text.primary, 0.08)}`,
		transition: theme.transitions.create(
			['background-color', 'box-shadow', 'border-color'],
			{duration: 200}
		),
		'&:hover': {
			borderColor: fade(theme.palette.text.primary, 0.16),
		},
		'&:focus-within': {
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.16)}`,
		},
	},
	searchIcon: {
		padding: theme.spacing(0, 1.5),
		height: '100%',
		position: 'absolute',
		pointerEvents: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: theme.palette.text.secondary,
	},
	inputRoot: {
		color: theme.palette.text.primary,
		width: '100%',
	},
	inputInput: {
		padding: theme.spacing(1.25, 1, 1.25, 0),
		paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
		fontSize: '0.9375rem',
		width: '100%',
	},
	checkAllBtn: {
		flexShrink: 0,
		textTransform: 'none',
		fontWeight: 600,
		color: theme.palette.text.primary,
		borderRadius: 8,
		padding: theme.spacing(1, 1.5),
		'&:hover': {
			backgroundColor: fade(theme.palette.text.primary, 0.06),
		},
	},
	dialogContent: {
		padding: 0,
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	listItem: {
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(2),
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		cursor: 'pointer',
		transition: theme.transitions.create('background-color', {duration: 150}),
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},
	childItem: {
		paddingLeft: theme.spacing(7),
		paddingRight: theme.spacing(2),
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		cursor: 'pointer',
		transition: theme.transitions.create('background-color', {duration: 150}),
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},
	listItemSelected: {
		backgroundColor: fade(theme.palette.primary.main, 0.08),
		'&:hover': {
			backgroundColor: fade(theme.palette.primary.main, 0.12),
		},
	},
	listItemPrimary: {
		fontSize: '0.9375rem',
		fontWeight: 500,
		color: theme.palette.text.primary,
	},
	listItemPrimarySelected: {
		fontWeight: 600,
	},
	childPrimary: {
		fontSize: '0.875rem',
		fontWeight: 400,
		color: theme.palette.text.primary,
	},
	listItemSecondary: {
		fontSize: '0.8125rem',
		color: theme.palette.text.secondary,
		marginTop: 2,
	},
	expandBtn: {
		padding: theme.spacing(0.5),
		color: theme.palette.text.secondary,
		'& .MuiIconButton-label': {
			display: 'flex',
		},
		'& svg': {
			display: 'block',
		},
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		padding: theme.spacing(6, 2),
		gap: theme.spacing(1),
		height: 400,
	},
	emptyIcon: {
		fontSize: 56,
		color: theme.palette.text.disabled,
		opacity: 0.4,
		marginBottom: theme.spacing(1),
	},
	emptyTitle: {
		fontSize: '1rem',
		fontWeight: 600,
		color: theme.palette.text.secondary,
	},
	emptySubtitle: {
		fontSize: '0.8125rem',
		color: theme.palette.text.disabled,
	},
	scrollContainer: {
		'&::-webkit-scrollbar': {width: 8},
		'&::-webkit-scrollbar-track': {backgroundColor: 'transparent'},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: fade(theme.palette.text.primary, 0.2),
			borderRadius: 4,
			'&:hover': {
				backgroundColor: fade(theme.palette.text.primary, 0.32),
			},
		},
		scrollbarWidth: 'thin',
		scrollbarColor: `${fade(theme.palette.text.primary, 0.2)} transparent`,
	},
	actions: {
		padding: theme.spacing(2, 3),
		gap: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	resetBtn: {
		color: theme.palette.error.main,
		fontWeight: 600,
		textTransform: 'none',
		'&:hover': {
			backgroundColor: fade(theme.palette.error.main, 0.08),
		},
	},
	cancelBtn: {
		textTransform: 'none',
		fontWeight: 500,
		borderRadius: 8,
	},
	doneBtn: {
		textTransform: 'none',
		fontWeight: 600,
		borderRadius: 8,
		boxShadow: 'none',
		'&:hover': {
			boxShadow: `0 4px 12px ${fade(theme.palette.primary.main, 0.32)}`,
		},
	},
	spacer: {flex: 1},
	checkbox: {
		padding: theme.spacing(1),
	},
}));

export default function VirtualListCBGroup({setClose, open, title, data, language, selectedClients, selectedUsers}) {
	const classes = useStyles();
	const [checked, setChecked] = useState({});
	const [expanded, setExpanded] = useState({});
	const [searchText, setSearchText] = useState('');

	useEffect(() => {
		if (!open) {
			return;
		}
		var tmp = {};
		var restricted = {};
		const users = selectedUsers || [];
		for (var i = 0; i < users.length; i++) {
			tmp[users[i].value] = true;
			restricted[String(users[i].value).split('|')[0]] = true;
		}
		const clients = selectedClients || [];
		for (var j = 0; j < clients.length; j++) {
			const code = clients[j].value;
			if (restricted[code]) {
				continue;
			}
			const group = (data || []).find(g => g.value === code);
			if (!group) {
				continue;
			}
			const kids = group.children || [];
			for (var k = 0; k < kids.length; k++) {
				tmp[kids[k].value] = true;
			}
		}
		setChecked(tmp);
		setExpanded({});
		setSearchText('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	function escapeRegex(string) {
		return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	}

	const search = JString(escapeRegex(String(searchText)));
	const isSearching = search.isValid();

	const matches = (text) => {
		const tokens = (search.str).split(' ');
		for (let i = 0; i < tokens.length; i++) {
			if (!JString(text).searchDeep(tokens[i])) {
				return false;
			}
		}
		return true;
	};

	const visibleGroups = [];
	for (let i = 0; i < (data || []).length; i++) {
		const group = data[i];
		const kids = group.children || [];
		if (!isSearching || matches(group.value + group.name)) {
			visibleGroups.push({group: group, children: kids});
			continue;
		}
		const hits = kids.filter(c => matches(c.value + c.name));
		if (hits.length > 0) {
			visibleGroups.push({group: group, children: hits});
		}
	}

	const rows = [];
	for (let i = 0; i < visibleGroups.length; i++) {
		const item = visibleGroups[i];
		rows.push({type: 'group', group: item.group});
		if (isSearching || expanded[item.group.value] === true) {
			for (let j = 0; j < item.children.length; j++) {
				rows.push({type: 'child', child: item.children[j]});
			}
		}
	}

	const groupState = (group) => {
		const kids = group.children || [];
		var count = 0;
		for (let i = 0; i < kids.length; i++) {
			if (checked[kids[i].value]) {
				count++;
			}
		}
		if (count === 0) {
			return 'none';
		}
		return count === kids.length ? 'all' : 'some';
	};

	const toggleGroup = (group) => {
		const kids = group.children || [];
		const state = groupState(group);
		var tmp = Object.assign({}, checked);
		for (let i = 0; i < kids.length; i++) {
			if (state === 'all') {
				delete tmp[`${kids[i].value}`];
			} else {
				tmp[`${kids[i].value}`] = true;
			}
		}
		setChecked(tmp);
	};

	const toggleChild = (child) => {
		var tmp = Object.assign({}, checked);
		if (child.value in tmp) {
			delete tmp[`${child.value}`];
		} else {
			tmp[`${child.value}`] = true;
		}
		setChecked(tmp);
	};

	const toggleExpand = (group) => {
		var tmp = Object.assign({}, expanded);
		tmp[`${group.value}`] = !tmp[group.value];
		setExpanded(tmp);
	};

	const checkAll = () => {
		var tmp = Object.assign({}, checked);
		var allOn = true;
		for (let i = 0; i < visibleGroups.length; i++) {
			const kids = visibleGroups[i].children;
			for (let j = 0; j < kids.length; j++) {
				if (!tmp[kids[j].value]) {
					allOn = false;
				}
			}
		}
		for (let i = 0; i < visibleGroups.length; i++) {
			const kids = visibleGroups[i].children;
			for (let j = 0; j < kids.length; j++) {
				if (allOn) {
					delete tmp[`${kids[j].value}`];
				} else {
					tmp[`${kids[j].value}`] = true;
				}
			}
		}
		setChecked(tmp);
	};

	const confirm = () => {
		const resultClients = [];
		const resultUsers = [];
		for (let i = 0; i < (data || []).length; i++) {
			const group = data[i];
			const kids = group.children || [];
			const picked = kids.filter(c => checked[c.value]);
			if (picked.length === 0) {
				continue;
			}
			resultClients.push({name: group.name, value: group.value});
			if (picked.length < kids.length) {
				for (let j = 0; j < picked.length; j++) {
					resultUsers.push({name: picked[j].name, value: picked[j].value});
				}
			}
		}
		setClose({clients: resultClients, users: resultUsers});
	};

	const closeReset = () => {
		setSearchText('');
		setExpanded({});
	};

	const RenderRow = ({props}) => {
		const {index, style} = props;
		const row = rows[index];
		if (!row) {
			return null;
		}
		if (row.type === 'group') {
			const state = groupState(row.group);
			const isOpen = isSearching || expanded[row.group.value] === true;
			return (
				<ListItem
					button
					style={style}
					key={index}
					onClick={() => toggleGroup(row.group)}
					className={`${classes.listItem} ${state !== 'none' ? classes.listItemSelected : ''}`}>
					<IconButton
						className={classes.expandBtn}
						size="small"
						disableRipple
						onClick={(e) => {
							e.stopPropagation();
							toggleExpand(row.group);
						}}>
						{isOpen ? <KeyboardArrowDownRoundedIcon/> : <KeyboardArrowRightRoundedIcon/>}
					</IconButton>
					<Checkbox
						id={row.group.value}
						className={classes.checkbox}
						tabIndex={-1}
						disableRipple
						inputProps={{'aria-labelledby': String(index)}}
						checked={state === 'all'}
						indeterminate={state === 'some'}
						color="primary"
					/>
					<ListItemText
						primary={row.group.name}
						secondary={row.group.secondary}
						primaryTypographyProps={{
							className: `${classes.listItemPrimary} ${state !== 'none' ? classes.listItemPrimarySelected : ''}`,
						}}
						secondaryTypographyProps={{
							className: classes.listItemSecondary,
						}}
					/>
				</ListItem>
			);
		}
		const isChecked = row.child.value in checked;
		return (
			<ListItem
				button
				style={style}
				key={index}
				onClick={() => toggleChild(row.child)}
				className={`${classes.childItem} ${isChecked ? classes.listItemSelected : ''}`}>
				<Checkbox
					id={row.child.value}
					className={classes.checkbox}
					tabIndex={-1}
					disableRipple
					inputProps={{'aria-labelledby': String(index)}}
					checked={isChecked}
					color="primary"
				/>
				<ListItemText
					primary={row.child.name}
					primaryTypographyProps={{
						className: `${classes.childPrimary} ${isChecked ? classes.listItemPrimarySelected : ''}`,
					}}
				/>
			</ListItem>
		);
	};

	const selectionCount = Object.keys(checked).length;

	return (
		<Dialog
			fullScreen={false}
			maxWidth="sm"
			fullWidth
			open={open}
			onClose={() => {}}
			PaperProps={{className: classes.paper}}
			aria-labelledby="group-list-dialog-title">
			<DialogTitle id="group-list-dialog-title" className={classes.dialogTitle} disableTypography>
				<div className={classes.titleRow}>
					<div className={classes.titleLeft}>
						<Typography component="div" className={classes.titlePrimary}>
							{title}
						</Typography>
						<div className={classes.selectionBadge}>
							{selectionCount} {language.selected}
						</div>
					</div>
				</div>
				<div className={classes.searchRow}>
					<div className={classes.search}>
						<div className={classes.searchIcon}>
							<SearchRoundedIcon/>
						</div>
						<InputBase
							value={searchText}
							placeholder={language.search}
							classes={{root: classes.inputRoot, input: classes.inputInput}}
							inputProps={{'aria-label': 'search'}}
							onChange={(e) => setSearchText(e.target.value)}
						/>
					</div>
					<Button onClick={() => checkAll()} size="small" className={classes.checkAllBtn}>
						{language.check_all}
					</Button>
				</div>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				{rows.length === 0 ? (
					<div className={classes.emptyState}>
						<SearchRoundedIcon className={classes.emptyIcon}/>
						<Typography className={classes.emptyTitle}>
							{language.no_matches_found}
						</Typography>
						<Typography className={classes.emptySubtitle}>
							{language.try_different_keyword}
						</Typography>
					</div>
				) : (
					<FixedSizeList
						height={400}
						width="100%"
						itemSize={56}
						itemCount={rows.length}
						className={classes.scrollContainer}>
						{children => <RenderRow props={children}/>}
					</FixedSizeList>
				)}
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button
					className={classes.resetBtn}
					onClick={() => {
						setClose({name: 'reset', value: ''});
						setChecked({});
						closeReset();
					}}
					size="small">
					{language.reset_selection}
				</Button>
				<div className={classes.spacer}/>
				<Button
					variant="outlined"
					className={classes.cancelBtn}
					onClick={() => {
						setClose(false);
						closeReset();
					}}>
					{language.cancel}
				</Button>
				<Button
					variant="contained"
					color="primary"
					className={classes.doneBtn}
					disabled={selectionCount === 0}
					onClick={() => {
						confirm();
						closeReset();
					}}>
					{language.done}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
